import { isPlatformServer, TitleCasePipe, NgIf, NgClass, AsyncPipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	HostBinding,
	Inject,
	Input,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	ViewChild,
} from '@angular/core';
import { ActivatedRoute, UrlSegment, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	AppSettingsService,
	BreakPointService,
	ButtonComponent,
	CallToActionResponse,
	CustomWindow,
	Device,
	FeatureService,
	NotificationType,
	SearchTarget,
	ShopperService,
	ScrollIntoViewDirective,
	TrackingEvent,
	TrackEventDirective,
	WappleLoadingComponent,
	WINDOW,
} from '@woolworthsnz/styleguide';
import {
	ContextResponse,
	FilterCriteria,
	ProductsListResponse,
	ProductResponse,
	SearchResponse,
} from '@woolworthsnz/trader-api';
import { Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import {
	FilterChangeArgs,
	SearchLibService,
	SearchMediatorService,
	SearchPageSizeLimits,
	SearchQueryParams,
	SearchState,
	SearchFooterComponent,
	PiesSearchHeaderComponent,
	SearchDasFilterComponent,
	SearchResultsHeaderComponent,
} from '@woolworthsnz/search';
import { DynamicContentProductService } from '../../services/dynamic-content-product.service';
import { CarouselComponent } from '../carousel/carousel.component';
import { SelectedDataSource } from '../../ui-models/carousel-data-source';

@Component({
	selector: 'dc-product-grid',
	templateUrl: 'product-grid.component.html',
	styleUrls: ['product-grid.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		ScrollIntoViewDirective,
		WappleLoadingComponent,
		SearchResultsHeaderComponent,
		SearchDasFilterComponent,
		PiesSearchHeaderComponent,
		CarouselComponent,
		NgClass,
		SearchFooterComponent,
		ButtonComponent,
		RouterLink,
		TrackEventDirective,
		AsyncPipe,
	],
})
export class ProductGridComponent implements OnInit, OnDestroy {
	device$ = this.breakPointService.device$.pipe(takeUntilDestroyed());
	@ViewChild('piesHeader', { read: ElementRef }) piesHeaderRef: ElementRef;

	@Input() name: string;
	@Input() title: string;
	@Input() titleIsMobileOnly: boolean;

	@HostBinding('attr.display-type')
	@Input()
	displayType: 'grid' | 'carousel';

	@Input() ctaEnabled: boolean;
	@Input() ctaAsHeader: boolean;
	@Input() ctaUrl: string;
	@Input() ctaLinkText: string;
	@Input() ctaImagePath: string;
	@Input() ctaImageAltText: string;

	@Input() productGroupId: string;
	@Input() productGroupSort: string;
	@Input() maxProductsCount: number;

	// Specify data source we want to get data from for product grid. E.g. specials sorted favourites
	@Input() selectedDataSource: string;
	@Input() dataSourceEnabled: boolean;

	@Input() analyticsID: string;

	@Input() filteringEnabled = false;
	@Input() filterPageSize = 120;
	@Input() filterDynamicTitleEnabled = false;

	get url(): UrlSegment[] {
		return this.route.snapshot && this.route.snapshot.url;
	}

	get dataSourceIsSpecialsSortedFavourites(): boolean {
		return this.dataSourceEnabled && this.selectedDataSource === SelectedDataSource.SpecialsSortedFavourites;
	}

	loading = true;
	searchState: SearchState = {
		sortOptions: [],
		search: '',
		groupId: 0,
		dynamicGroup: '',
		dasFacets: [],
		facets: [],
		selectedFilters: [],
		routeUrl: '',
		scrollToTop: false,
		currentPage: 1,
		currentPageSize: 24,
		currentSortOption: 'BrowseRelevance',
		inStockProductsOnly: true,
	};

	device = Device;
	currentDevice: Device;
	piesHeaderHeight = 0;
	filterGroups: FilterCriteria[][] = [[]];

	// Product Information Enrichment filters
	selectedPiesFilters: FilterCriteria[] = [];

	// Product Department / Aisle / Shelve filters
	selectedDasFilters: FilterCriteria[] = [];
	lastSelectedDasFilter?: FilterCriteria;

	isShowInStockItemsOnlyFeatureEnabled$: Observable<boolean>;
	isHideSwitchToggleForOOSEnabled$: Observable<boolean>;

	itemsPerPageCollection: string[] = [];
	minimumPageSize: number;
	dasTitle?: string;
	totalItems = 0;

	isLoggedIn$: Observable<boolean | undefined>;
	isShopper$: Observable<boolean | undefined>;
	products$: Observable<ProductResponse[] | undefined>;
	cta: CallToActionResponse | undefined;
	hero: CallToActionResponse | undefined;
	showFooterCta = false;
	notificationType = NotificationType;
	trackingEvent = TrackingEvent;
	private filtersChangedSub: Subscription;
	private isMobileDevice = false;

	private readonly productsPerRow = 5;
	private rowCount = 1;
	private destroyed$: Subject<boolean> = new Subject();

	// ------------------------------------------------------------

	constructor(
		private shopperService: ShopperService,
		private dynamicContentProductService: DynamicContentProductService,
		private searchLibService: SearchLibService,
		public breakPointService: BreakPointService,
		private cdr: ChangeDetectorRef,
		private featureService: FeatureService,
		private searchMediatorService: SearchMediatorService,
		private route: ActivatedRoute,
		private titleCasePipe: TitleCasePipe,
		appSettingsService: AppSettingsService,
		@Inject(WINDOW) private window: CustomWindow,
		@Inject(PLATFORM_ID) private platformId: Object
	) {
		this.isLoggedIn$ = this.shopperService.state$.pipe(
			map((s) => s.isLoggedIn || this.window.BOOTSTRAP_DATA?.isLoggedIn)
		);
		this.isShopper$ = this.shopperService.state$.pipe(
			map((s) => s.isShopper || this.window.BOOTSTRAP_DATA?.isShopperLoggedIn)
		);
		this.itemsPerPageCollection = appSettingsService.getSetting('ITEMS_PER_PAGE');
		this.minimumPageSize = appSettingsService.getSetting('MINIMUM_PAGE_SIZE');

		this.breakPointService.device$.pipe(takeUntilDestroyed()).subscribe((device) => (this.currentDevice = device));
	}

	ngOnInit(): void {
		this.isLoggedIn$.pipe(takeUntil(this.destroyed$)).subscribe((isLoggedIn): void => {
			if (isLoggedIn && this.dataSourceIsSpecialsSortedFavourites) {
				this.displayType = 'grid';
				this.rowCount = 2;
				this.maxProductsCount = this.rowCount * this.productsPerRow;
				this.name = 'Top Picks';
			}
		});
		this.searchState.currentPageSize = this.filterPageSize;

		this.device$.subscribe((device): void => {
			this.isMobileDevice = device === Device.MOBILE;
		});

		this.fetchProducts();

		// Show the footer CTA button if CTA enabled.
		// In desktop carousel mode, the CTA is rendered as a tile, so then we don't want to show the CTA button
		this.showFooterCta =
			this.ctaEnabled && !this.ctaAsHeader && (this.isMobileDevice || this.displayType === 'grid');

		if (this.ctaEnabled) {
			if (this.ctaAsHeader) {
				this.hero = {
					image: {
						src: this.ctaImagePath,
						alt: this.ctaImageAltText,
					},
					action: {
						title: this.ctaLinkText || '',
						link: this.ctaUrl,
					},
				};
				return;
			}

			this.cta = {
				image: {
					src: this.ctaImagePath,
					alt: this.ctaImageAltText,
				},
				action: {
					title: this.ctaLinkText || '',
					link: this.ctaUrl,
				},
			};
		}

		this.isShowInStockItemsOnlyFeatureEnabled$ = this.featureService.isEnabled(
			ContextResponse.EnabledFeaturesEnum.SwitchToggleToShowInStockItemsOnly
		);

		this.isHideSwitchToggleForOOSEnabled$ = this.featureService.isEnabled(
			ContextResponse.EnabledFeaturesEnum.HideSwitchToggleForOOS
		);

		this.subscribeToEvents();
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
		if (this.filtersChangedSub) {
			this.filtersChangedSub.unsubscribe();
		}
	}

	fetchProductsFromDatasource(): Observable<ProductResponse[] | undefined> {
		// this api call is fairly expensive - so we'll just return placeholders on SSR and let the browser do the work later
		if (isPlatformServer(this.platformId)) {
			const numPlaceholders = 5;
			const placeholderProduct: ProductResponse = {};
			placeholderProduct.name = '';
			this.setLoading(false);
			return of(Array(numPlaceholders).fill(placeholderProduct));
		}

		return this.isLoggedIn$.pipe(
			distinctUntilChanged(),
			switchMap((isLoggedIn) => {
				if (isLoggedIn && this.dataSourceIsSpecialsSortedFavourites) {
					return this.getPersonalisedProducts();
				}

				if (this.filteringEnabled) {
					return this.fetchSearchProductGroup();
				}

				return this.getDynamicContentProductGroup();
			}),
			tap(() => this.setLoading(false)),
			catchError((err) => {
				this.setLoading(false);
				return of(err);
			})
		);
	}

	getPersonalisedProducts(): Observable<ProductResponse[] | undefined> {
		return this.dynamicContentProductService.getProductsSpecials().pipe(
			map((res: ProductsListResponse) =>
				res.products?.items?.map((product: ProductResponse) => ({
					...product,
				}))
			),
			map((products) =>
				products && products?.length > this.maxProductsCount
					? products?.slice(0, this.maxProductsCount)
					: products
			)
		);
	}

	onDasFilterChanged(args: FilterChangeArgs): void {
		this.toggleSearchFilter(this.selectedPiesFilters, args.filter);
	}

	onDasFacetChanged(dasFilter: FilterCriteria): void {
		this.toggleSearchFilter(this.selectedDasFilters, dasFilter);

		if (this.selectedDasFilters.length > 0) {
			const lastDasFilter = this.selectedDasFilters[this.selectedDasFilters.length - 1];
			this.dasTitle = lastDasFilter.name;
			this.lastSelectedDasFilter = lastDasFilter;
		} else {
			this.dasTitle = undefined;
			this.lastSelectedDasFilter = undefined;
		}
	}

	onClearFilters(): void {
		this.lastSelectedDasFilter = undefined;
		this.dasTitle = undefined;
		this.selectedPiesFilters = [];
		this.selectedDasFilters = [];
		this.searchState.currentPage = 1;
		this.fetchProducts();
	}

	onRemoveFilter(filterCriteria: FilterCriteria): void {
		this.toggleSearchFilter(this.selectedPiesFilters, filterCriteria);
	}

	onRemoveAllFilters(): void {
		this.onClearFilters();
	}

	onSortChanged(sortOption: string): void {
		this.searchState.currentSortOption = sortOption;
		this.fetchProducts();
	}

	get headerTitle(): string {
		if (this.filteringEnabled) {
			// If not on a mobile device and the product grid has been configured to only display
			// on mobile devices, then return a blank title
			if (!this.isMobileDevice && this.titleIsMobileOnly) {
				return ' '; // Need to return a space, else the total items label will also not be displayed
			}

			if (this.filterDynamicTitleEnabled && this.dasTitle) {
				if (this.title) {
					return `${this.title} - ${this.dasTitle}`;
				} else {
					return this.dasTitle;
				}
			}

			return this.title || ' ';
		} else {
			return this.title;
		}
	}

	private fetchSearchProductGroup(): Observable<ProductResponse[] | undefined> {
		const servicePayload: SearchQueryParams = {
			dasFilter: this.searchLibService.buildFilterQuery(this.selectedDasFilters),
			target: SearchTarget.ProductGroup,
			filters: this.searchLibService.buildFilterQuery(this.selectedPiesFilters),
			groupId: Number(this.productGroupId),
			dynamicGroup: '',
			page: this.searchState.currentPage,
			size: this.searchState.currentPageSize,
			sort: this.searchState.currentSortOption,
		};

		return this.searchLibService.fetchProducts(servicePayload).pipe(
			tap((res: SearchResponse) => {
				this.searchState.dasFacets = res.dasFacets ?? [];
				this.searchState.facets = res.facets ?? [];
				this.searchState.sortOptions = res.sortOptions ?? [];
				this.totalItems = res.products?.totalItems ?? 0;

				if (this.searchState.dasFacets) {
					this.searchState.dasFacets.forEach(
						(dasFacet: FilterCriteria) =>
							(dasFacet.name = this.titleCasePipe.transform(dasFacet.name) || '')
					);
				}

				if (this.searchState.facets) {
					this.searchState.facets.forEach(
						(facet: FilterCriteria) => (facet.name = this.titleCasePipe.transform(facet.name) || '')
					);

					this.filterGroups = this.searchLibService.getFilteredGroups(this.searchState.facets);
				}

				this.searchState.currentSortOption = res.currentSortOption || 'BrowseRelevance';
				this.searchState.currentPageSize = res.currentPageSize ?? SearchPageSizeLimits.Minimum;
			}),
			map((response) => {
				if (response?.products) {
					const productItems = response.products.items as ProductResponse[];
					return this.dataSourceIsSpecialsSortedFavourites && productItems.length > this.maxProductsCount
						? productItems.slice(0, this.maxProductsCount)
						: productItems;
				}

				return undefined;
			})
		);
	}

	private getDynamicContentProductGroup(): Observable<ProductResponse[] | undefined> {
		return this.dynamicContentProductService
			.searchProductsByProductGroups(this.productGroupId.toString(), {
				productGroupSort: this.productGroupSort,
				maxProductsCount: this.maxProductsCount,
			})
			.pipe(map((res) => res.items));
	}

	private setLoading(loading: boolean): void {
		this.loading = loading;
		setTimeout(() => {
			this.setNavTopMargin();
			this.cdr.markForCheck();
		});
		this.cdr.markForCheck();
	}

	private subscribeToEvents(): void {
		this.searchMediatorService.filterSelection$
			.pipe(takeUntil(this.destroyed$))
			.subscribe((filters: FilterCriteria[]) => {
				this.selectedPiesFilters = filters;
				this.searchState.currentPage = 1;
				this.fetchProducts();
			});

		this.searchMediatorService.pageSizeChange$.pipe(takeUntil(this.destroyed$)).subscribe((size: number) => {
			this.searchState.currentPageSize = size;
			this.searchState.currentPage = 1;
			this.fetchProducts();
		});

		this.searchMediatorService.pageChange$.pipe(takeUntil(this.destroyed$)).subscribe((page: number) => {
			this.searchState.currentPage = page;
			this.fetchProducts();
		});

		this.searchMediatorService.sortChange$.pipe(takeUntil(this.destroyed$)).subscribe((sortOption: string) => {
			this.searchState.currentSortOption = sortOption;
			this.fetchProducts();
		});

		this.searchMediatorService.loadMore$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
			this.searchState.currentPage = this.searchState.currentPage + 1;
			this.fetchProducts();
		});

		this.searchMediatorService.scrollToTop$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
			this.scrollToTop();
		});

		this.searchMediatorService.removeFilter$
			.pipe(takeUntil(this.destroyed$))
			.subscribe((filterCriteria: FilterCriteria) => {
				this.selectedPiesFilters = this.selectedPiesFilters.filter((f) => f.key !== filterCriteria.key);
				this.searchState.currentPage = 1;
				this.fetchProducts();
			});

		this.searchMediatorService.removeAllFilters$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
			this.selectedPiesFilters = [];
			this.searchState.currentPage = 1;
			this.fetchProducts();
		});
	}

	private fetchProducts(): void {
		this.setLoading(true);
		this.products$ = this.fetchProductsFromDatasource();
	}

	private scrollToTop(): void {
		this.searchState.scrollToTop = true;
		this.cdr.markForCheck();

		setTimeout(() => {
			this.searchState.scrollToTop = false;
			this.cdr.markForCheck();
		}, 10);
	}

	private setNavTopMargin(): void {
		if (
			this.piesHeaderRef &&
			this.piesHeaderRef.nativeElement &&
			this.piesHeaderHeight !== this.piesHeaderRef.nativeElement.offsetHeight
		) {
			this.piesHeaderHeight = this.piesHeaderRef.nativeElement.offsetHeight;
			this.cdr.markForCheck();
		}
	}

	private toggleSearchFilter(searchFilters: FilterCriteria[], filterCriteria: FilterCriteria): void {
		const index = searchFilters.findIndex((f: FilterCriteria) => f.name === filter.name);
		if (index === -1) {
			searchFilters.push(filterCriteria);
		} else {
			searchFilters.splice(index, 1);
		}

		this.searchState.currentPage = 1;
		this.fetchProducts();
	}
}
